import React from 'react';
import { DataView } from 'primereact/dataview';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Text } from '@components/Text';
import {
  closeCurrentModalAction,
  setCurrentModalAction,
} from 'src/redesign/features/Modals/store/reducer';
import { CONNECTED_MODALS } from 'src/redesign/features/Modals/store/constants';
import { useAppDispatch } from 'src/hooks';
import moment from 'moment';
import c from 'clsx';
import S from './HostNotificationsModal.module.scss';

interface HostNotificationsModalProps {
  requests: any[];
}

export const HostNotificationsModal: React.FC<HostNotificationsModalProps> = ({ requests }) => {
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(closeCurrentModalAction());
  };

  const handleViewMessage = (message: any) => {
    dispatch(
      setCurrentModalAction({
        currentModal: CONNECTED_MODALS.CONTACT_MESSAGE_MODAL,
        data: {
          title: 'Contact Message',
          message,
        },
      }),
    );
  };

  const handleViewRequest = (request: any) => {
    const modalType =
      request.type === 'refund'
        ? CONNECTED_MODALS.CANCEL_REFUND_REQUEST_MODAL
        : CONNECTED_MODALS.TRANSFER_REQUEST_MODAL;

    dispatch(
      setCurrentModalAction({
        currentModal: modalType,
        data: {
          title: request.type === 'refund' ? 'Cancellation & Refund Request' : 'Transfer Request',
          request,
        },
      }),
    );
  };

  const getStatusBadge = (status: string) => {
    const severityMap: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
      pending: 'warning',
      replied: 'success',
      approved: 'success',
      denied: 'danger',
    };
    return (
      <Badge
        value={status.charAt(0).toUpperCase() + status.slice(1)}
        severity={severityMap[status] || 'info'}
      />
    );
  };

  const itemTemplate = (request: any) => {
    // Contact Message template
    if (request.type === 'CONTACT_MESSAGE') {
      return (
        <div className={S.listItem} onClick={() => handleViewMessage(request)}>
          <div className={S.itemHeader}>
            <div className={S.itemLeft}>
              <i className="pi pi-envelope" style={{ fontSize: '1.25rem', color: '#8b5cf6' }} />
              <div className={S.itemInfo}>
                <Text variation="body" bold>
                  {request.sender.name}
                </Text>
                <Text variation="body-small" color="black-400">
                  {request.subject}
                </Text>
              </div>
            </div>
            <div className={S.itemRight}>
              {getStatusBadge(request.status)}
              <Text variation="caption" color="black-400">
                {moment(request.created_at).fromNow()}
              </Text>
            </div>
          </div>
          <div className={S.itemPreview}>
            <Text variation="body-small" color="black-400">
              {request.message.length > 100
                ? `${request.message.substring(0, 100)}...`
                : request.message}
            </Text>
          </div>
        </div>
      );
    }

    // Refund/Transfer Request template
    const isRefund = request.type === 'refund';
    const attendeeName = request.attendees?.[0]
      ? `${request.attendees[0].first_name} ${request.attendees[0].last_name}`
      : 'Unknown';

    return (
      <div className={S.listItem} onClick={() => handleViewRequest(request)}>
        <div className={S.itemHeader}>
          <div className={S.itemLeft}>
            <i
              className={isRefund ? 'pi pi-times-circle' : 'pi pi-sync'}
              style={{ fontSize: '1.25rem', color: isRefund ? '#ef4444' : '#3b82f6' }}
            />
            <div className={S.itemInfo}>
              <Text variation="body" bold>
                {attendeeName}
              </Text>
              <Text variation="body-small" color="black-400">
                {isRefund ? 'Cancellation & Refund' : 'Transfer Request'} • Order #
                {request.cart?.friendly_cart_id}
              </Text>
            </div>
          </div>
          <div className={S.itemRight}>
            {getStatusBadge(request.status)}
            <Text variation="caption" color="black-400">
              {moment(request.created_at).fromNow()}
            </Text>
          </div>
        </div>
        <div className={S.itemPreview}>
          <Text variation="body-small" color="black-400">
            <strong>Event:</strong> {request.event?.title} • <strong>Reason:</strong>{' '}
            {request.reason.length > 80 ? `${request.reason.substring(0, 80)}...` : request.reason}
          </Text>
        </div>
      </div>
    );
  };

  if (!requests || requests.length === 0) {
    return (
      <div className={S.modalContent}>
        <div className={S.emptyState}>
          <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#9ca3af' }} />
          <Text variation="h4" bold>
            No Pending Requests
          </Text>
          <Text variation="body" color="black-400">
            You have no pending notifications at this time.
          </Text>
          <Button
            label="Close"
            onClick={handleClose}
            className="p-button-text"
            style={{ color: 'white' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={S.modalContent}>
      <DataView value={requests} itemTemplate={itemTemplate} className={S.dataView} />
      <div className={S.modalFooter}>
        <Button
          label="Close"
          onClick={handleClose}
          className="p-button-text"
          style={{ color: 'white' }}
        />
      </div>
    </div>
  );
};
